/**
 * CelebrationModal — splash-модал мини-празднования (рубеж организации).
 *
 * Показывается один раз на пользователя, когда орга берёт рубеж из реестра
 * services/milestones.js (первый — 100 созданных Работ). Универсальный:
 * тексты выбираются по metric+threshold из i18n, компонент про метрику ничего
 * не знает. Новая метрика = новые ключи milestone.<metric>.* и запись в реестре.
 *
 * Содержание намеренно не повторяет дашборд: объекты/клиенты владелец видит
 * каждый день. Здесь — цифры пути, которых он не считал (старт, темп, лучший
 * месяц, автор юбилейной Работы) и следующая цель, чтобы праздник не был тупиком.
 *
 * Конфетти — свой canvas, без новой зависимости во фронте (canvas-confetti тянет
 * ~7КБ и свой RAF-цикл ради одного экрана в год). Три волны: два боковых залпа
 * сразу, третий сверху с задержкой — так «салют» держится весь показ, а не
 * схлопывается за секунду. Уважает prefers-reduced-motion: при включённой
 * настройке анимации нет, остаётся статичный модал.
 *
 * Все строки — через t(). PostHog: milestone_shown / milestone_dismissed /
 * milestone_cta_clicked + data-ph-id на кнопках.
 */
import { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { Analytics } from '../analytics';
import { BRAND_NAME } from '../branding';

const COLORS = ['#4f8ef7', '#34c77b', '#f59e0b', '#a855f7', '#f05252', '#22d3ee', '#ffffff'];
const DURATION_MS = 5600;
// Волны салюта: [частиц, задержка мс, тип]. Боковые пушки + верхний «дождь».
// 600 частиц — потолок: это 600 fillRect на кадр, дальше на слабых Android
// кадр перестаёт укладываться в 16 мс и салют начинает дёргаться.
const WAVES = [
  { count: 220, delay: 0,    kind: 'cannon' },
  { count: 180, delay: 550,  kind: 'cannon' },
  { count: 120, delay: 1100, kind: 'rain'   },
  { count: 80,  delay: 1800, kind: 'rain'   },
];

function reducedMotion() {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

function useConfetti(canvasRef, settledRef, active) {
  useEffect(() => {
    if (!active || reducedMotion()) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    // Второй холст — «осевшее» конфетти внутри шапки. Живёт под текстом (z=0
    // против z=1 у цифры) и НЕ очищается: то, что на него попало, остаётся до
    // закрытия модала. Ради этого экрана всё и затевалось — юзер его скриншотит.
    const settled = settledRef.current;
    const sctx = settled ? settled.getContext('2d') : null;

    const resize = () => {
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (settled && sctx) {
        // Ресайз холста стирает его содержимое, поэтому осевшие частицы храним
        // отдельным списком и перерисовываем.
        settled.width = settled.offsetWidth * dpr;
        settled.height = settled.offsetHeight * dpr;
        sctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        redrawSettled();
      }
    };

    const stuck = [];
    const drawPiece = (c, p, alpha) => {
      c.save();
      c.globalAlpha = alpha;
      c.translate(p.x, p.y);
      c.rotate(p.rot);
      c.fillStyle = p.color;
      c.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      c.restore();
    };
    function redrawSettled() {
      if (!sctx || !settled) return;
      sctx.clearRect(0, 0, settled.offsetWidth, settled.offsetHeight);
      // 0.5 — компромисс: ниже и конфетти не видно на градиенте, выше и белая
      // цифра начинает конкурировать с фоном за внимание.
      stuck.forEach(p => drawPiece(sctx, p, 0.5));
    }

    resize();
    window.addEventListener('resize', resize);

    const W = () => canvas.offsetWidth;
    const H = () => canvas.offsetHeight;
    const headerH = () => (settled ? settled.offsetHeight : 0);
    const parts = [];

    const spawn = (wave) => {
      for (let i = 0; i < wave.count; i++) {
        if (wave.kind === 'rain') {
          // Дождь: сыплется по всей ширине сверху, медленнее пушек.
          // Часть падающих помечаем «прилипалами»: долетев до своей высоты в
          // шапке, они переезжают на нижний холст и остаются там навсегда.
          // Высота у каждого своя — иначе все застряли бы одной полосой у
          // верхней кромки, где их впервые проверили.
          const hh = headerH();
          const settler = !!sctx && hh > 60 && Math.random() < 0.45;
          parts.push({
            x: Math.random() * W(), y: -20 - Math.random() * H() * 0.5,
            vx: (Math.random() - 0.5) * 2.2, vy: 1.5 + Math.random() * 2.5,
            w: 4 + Math.random() * 6, h: 7 + Math.random() * 8,
            rot: Math.random() * Math.PI, vrot: (Math.random() - 0.5) * 0.3,
            gravity: 0.08, born: performance.now(),
            settler, stickY: settler ? 14 + Math.random() * (hh - 28) : null,
            color: COLORS[Math.floor(Math.random() * COLORS.length)],
          });
        } else {
          const fromLeft = i % 2 === 0;
          const angle = (fromLeft ? -60 : -120) * (Math.PI / 180) + (Math.random() - 0.5) * 1.0;
          const speed = 9 + Math.random() * 11;
          parts.push({
            x: fromLeft ? W() * 0.1 : W() * 0.9, y: H() * 0.3,
            vx: Math.cos(angle) * speed * (fromLeft ? 1 : -1) * -1,
            vy: Math.sin(angle) * speed,
            w: 5 + Math.random() * 7, h: 8 + Math.random() * 9,
            rot: Math.random() * Math.PI, vrot: (Math.random() - 0.5) * 0.4,
            gravity: 0.32, born: performance.now(),
            color: COLORS[Math.floor(Math.random() * COLORS.length)],
          });
        }
      }
    };

    const timers = WAVES.map(w => (w.delay ? setTimeout(() => spawn(w), w.delay) : (spawn(w), null)));

    let raf;
    const start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      ctx.clearRect(0, 0, W(), H());
      for (const p of parts) {
        if (p.stuck) continue;
        p.vy += p.gravity;
        p.vx *= 0.993;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vrot;

        // Прилипала долетел до своей высоты — переносим на холст шапки.
        if (p.settler && p.y >= p.stickY) {
          p.stuck = true;
          stuck.push({ x: p.x, y: p.stickY, rot: p.rot, w: p.w, h: p.h, color: p.color });
          drawPiece(sctx, { ...p, y: p.stickY }, 0.5);
          continue;
        }

        // Затухание считаем от рождения частицы, иначе поздние волны гаснут,
        // не успев долететь до низа карточки.
        const life = Math.max(0, 1 - (now - p.born) / (DURATION_MS * 0.8));
        if (life <= 0) continue;
        drawPiece(ctx, p, life);
      }
      if (elapsed < DURATION_MS) raf = requestAnimationFrame(tick);
      else ctx.clearRect(0, 0, W(), H());
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(tm => tm && clearTimeout(tm));
      window.removeEventListener('resize', resize);
    };
  }, [canvasRef, settledRef, active]);
}

function Row({ label, value }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
      padding: '9px 0', borderBottom: '1px solid var(--border)',
    }}>
      <span style={{ fontSize: 13, color: 'var(--text2)', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
  );
}

export default function CelebrationModal({ milestone, onClose }) {
  // Плавающая «+» прячется на время модала — общий механизм, см. useFabState.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('fab-hide'));
    return () => window.dispatchEvent(new CustomEvent('fab-show'));
  }, []);

  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const canvasRef = useRef(null);
  const settledRef = useRef(null);
  const downOnBackdrop = useRef(false);
  const {
    metric, threshold, stats, triggered_by_name: author,
    triggered_by_self: authorIsMe, next_threshold: nextThreshold, remaining,
  } = milestone;

  useConfetti(canvasRef, settledRef, true);

  useEffect(() => {
    Analytics.milestoneShown({ metric, threshold, value_at: milestone.value_at, preview: !!milestone.preview });
    // eslint-disable-next-line
  }, []);

  const dismiss = useCallback((source) => {
    Analytics.milestoneDismissed({ metric, threshold, source });
    onClose();
  }, [metric, threshold, onClose]);

  // Escape — на keyup, а не keydown: на keydown модал съедал бы то же нажатие,
  // которым пользователь закрывал предыдущий слой (общий паттерн проекта).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') dismiss('escape'); };
    window.addEventListener('keyup', onKey);
    return () => window.removeEventListener('keyup', onKey);
  }, [dismiss]);

  const locale = i18n.language === 'en' ? 'en-US' : 'ru-RU';
  const fmtDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'long' }); }
    catch { return null; }
  };
  const fmtMonth = (ym) => {
    if (!ym) return null;
    const [y, m] = String(ym).split('-');
    try { return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(locale, { month: 'long' }); }
    catch { return null; }
  };

  // Число прокидываем как {{value}}, а не {{count}}: count включил бы плюрализацию
  // i18next и потребовал бы _many/_other-ключи там, где текст от числа не зависит.
  const title = t([`milestone.${metric}.title`, 'milestone.default.title'], { value: threshold, brand: BRAND_NAME });
  // Обращение по имени — только имя, без фамилии: «Андрей Петров, ваша команда…»
  // звучит как письмо из банка. Если имени нет — отдельный безличный вариант.
  const firstName = (user?.name || '').trim().split(/\s+/)[0] || '';
  const lead = firstName
    ? t([`milestone.${metric}.lead`, 'milestone.default.lead'], { name: firstName })
    : t([`milestone.${metric}.leadNoName`, 'milestone.default.leadNoName']);

  const firstAt = fmtDate(stats?.first_task_at);
  const peak = fmtMonth(stats?.peak_month);
  const progress = nextThreshold ? Math.min(100, Math.round((threshold / nextThreshold) * 100)) : 0;

  return (
    <div
      onPointerDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (e.target === e.currentTarget && downOnBackdrop.current) dismiss('backdrop'); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12,
        backdropFilter: 'blur(2px)',
      }}
    >
      <div
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative', width: '100%', maxWidth: 400, maxHeight: '94vh',
          overflow: 'hidden', display: 'flex', flexDirection: 'column',
          borderRadius: 20, background: 'var(--bg2)', border: '1px solid var(--border)',
          boxShadow: '0 24px 70px rgba(0,0,0,.45)',
          animation: reducedMotion() ? 'none' : 'milestonePop .38s cubic-bezier(.2,.9,.3,1.4)',
        }}
      >
        {/* Конфетти летит НАД всем содержимым карточки (z выше шапки и блока
            строк), но pointerEvents:none — клики по кнопке проходят насквозь. */}
        <canvas
          ref={canvasRef}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 10 }}
        />

        {/* Шапка: цифра — герой экрана. Внутри два слоя: холст осевшего конфетти
            (z=0) и сам текст (z=1). Осевшее остаётся на экране после салюта —
            это то, что попадёт в скриншот. */}
        <div style={{
          flexShrink: 0,
          position: 'relative', overflow: 'hidden',
          padding: '32px 24px 22px', textAlign: 'center',
          background: 'linear-gradient(160deg, var(--accent) 0%, var(--purple) 100%)',
          borderRadius: '19px 19px 0 0',
        }}>
          <canvas
            ref={settledRef}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }}
          />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              fontSize: 60, fontWeight: 800, lineHeight: 1, color: '#fff',
              letterSpacing: '-0.03em', textShadow: '0 4px 20px rgba(0,0,0,.25)',
            }}>{threshold}</div>
            <div style={{ marginTop: 8, fontSize: 17, fontWeight: 700, color: '#fff' }}>{title}</div>
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 22px 0', position: 'relative', zIndex: 3 }}>
          <div style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.5, textAlign: 'center' }}>{lead}</div>

          <div style={{ marginTop: 16, background: 'var(--bg3)', borderRadius: 12, padding: '4px 14px' }}>
            {firstAt && <Row label={t('milestone.rowFirst')} value={firstAt} />}
            {stats?.per_month != null && (
              <Row label={t('milestone.rowPace')} value={t('milestone.rowPaceValue', { value: stats.per_month })} />
            )}
            {peak && stats?.peak_count != null && (
              <Row label={t('milestone.rowPeak')} value={`${peak} — ${stats.peak_count}`} />
            )}
            {author && (
              <Row
                label={t('milestone.rowAuthor', { value: threshold })}
                value={authorIsMe ? t('milestone.rowAuthorSelf') : author}
              />
            )}
          </div>

          {nextThreshold && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 7 }}>
                <span style={{ fontSize: 12, color: 'var(--text2)' }}>
                  {t('milestone.nextGoal', { value: nextThreshold })}
                </span>
                <span style={{ fontSize: 12, color: 'var(--text2)' }}>
                  {t('milestone.nextRemaining', { value: Math.max(0, remaining ?? 0) })}
                </span>
              </div>
              <div style={{ height: 6, borderRadius: 3, background: 'var(--bg3)', overflow: 'hidden' }}>
                <div style={{ width: `${progress}%`, height: '100%', background: 'var(--accent)', borderRadius: 3 }} />
              </div>
            </div>
          )}

        </div>

        {/* Кнопка — вне скролл-зоны, всегда в досягаемости */}
        <div style={{ flexShrink: 0, padding: '16px 22px 22px', position: 'relative', zIndex: 3 }}>
          <button
            type="button"
            data-ph-id="milestone-cta"
            className="btn btn-primary"
            onClick={() => { Analytics.milestoneCtaClicked({ metric, threshold }); onClose(); }}
            style={{ width: '100%', height: 44, fontSize: 15, fontWeight: 600 }}
          >
            {t('milestone.cta')}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes milestonePop {
          0%   { opacity: 0; transform: scale(.86) translateY(14px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
